/**
 * Sheet Component V2
 *
 * Slide-over panel (ersetzt Bootstrap Offcanvas)
 * WCAG 2.1 Level AA + BFSG konform
 * Built with Radix UI Dialog for accessibility
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Sheet Root & Trigger
// ============================================================================

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

// ============================================================================
// Sheet Overlay
// ============================================================================

const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50',
      'bg-black/50',
      // Animation
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));

SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

// ============================================================================
// Sheet Content
// ============================================================================

type SheetSide = 'top' | 'right' | 'bottom' | 'left';

const sheetVariants: Record<SheetSide, string> = {
  top: cn(
    'inset-x-0 top-0 border-b',
    'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top'
  ),
  bottom: cn(
    'inset-x-0 bottom-0 border-t',
    'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
  ),
  left: cn(
    'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
    'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left'
  ),
  right: cn(
    'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
    'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
  ),
};

interface SheetContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Side from which the sheet slides in
   * @default "right"
   */
  side?: SheetSide;
  /**
   * Show close button (X)
   * @default true
   */
  showCloseButton?: boolean;
}

const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, showCloseButton = true, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 gap-4 p-6',
        'bg-white',
        'border-neutral-200',
        'shadow-lg',
        // Animation
        'transition ease-in-out',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:duration-300 data-[state=open]:duration-500',
        // WCAG 2.1: Focus trap
        'focus:outline-none',
        sheetVariants[side],
        className
      )}
      {...props}
    >
      {children}

      {/* Close Button */}
      {showCloseButton && (
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4',
            'rounded-sm opacity-70',
            'ring-offset-background transition-opacity',
            'hover:opacity-100',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'disabled:pointer-events-none',
            // BFSG: Touch target
            'min-h-[44px] min-w-[44px]',
            'inline-flex items-center justify-center'
          )}
          aria-label={t('common.close')}
        >
          <X className="h-5 w-5 text-neutral-500" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </SheetPortal>
  );
});

SheetContent.displayName = 'SheetContent';

// ============================================================================
// Sheet Header
// ============================================================================

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-2 text-center sm:text-left',
      className
    )}
    {...props}
  />
);

SheetHeader.displayName = 'SheetHeader';

// ============================================================================
// Sheet Footer
// ============================================================================

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end gap-2',
      className
    )}
    {...props}
  />
);

SheetFooter.displayName = 'SheetFooter';

// ============================================================================
// Sheet Title
// ============================================================================

const SheetTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold',
      'text-neutral-900',
      className
    )}
    {...props}
  />
));

SheetTitle.displayName = DialogPrimitive.Title.displayName;

// ============================================================================
// Sheet Description
// ============================================================================

const SheetDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      'text-sm',
      'text-neutral-600',
      className
    )}
    {...props}
  />
));

SheetDescription.displayName = DialogPrimitive.Description.displayName;

// ============================================================================
// Exports
// ============================================================================

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  type SheetContentProps,
  type SheetSide,
};

/**
 * Sheet Component Usage
 *
 * @example
 * ```tsx
 * <Sheet>
 *   <SheetTrigger asChild>
 *     <Button variant="outline">Verlauf öffnen</Button>
 *   </SheetTrigger>
 *   <SheetContent side="right">
 *     <SheetHeader>
 *       <SheetTitle>Versionsverlauf</SheetTitle>
 *       <SheetDescription>
 *         Alle Änderungen an diesem Produkt
 *       </SheetDescription>
 *     </SheetHeader>
 *
 *     <div className="flex-1 overflow-y-auto py-4">
 *       {versions.map(v => <VersionCard key={v.id} {...v} />)}
 *     </div>
 *
 *     <SheetFooter>
 *       <SheetClose asChild>
 *         <Button variant="ghost">Schließen</Button>
 *       </SheetClose>
 *     </SheetFooter>
 *   </SheetContent>
 * </Sheet>
 * ```
 */
